import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import DebriefingVideo from "./debriefing-video/DebriefingVideo";
import DebriefingChecklistView from "./debriefing-checklist/DebriefingChecklistView";
import DebriefingService from "../../services/DebriefingService";
import CheckListService from "../../services/CheckListService";
import useChecklistAiResult from "../hooks/useChecklistAiResult";
import palette from "../../common/styles/palette";

import { Grid } from "@mui/material";

// A aba Checklist é a Correção: o professor percorre os Itens e dá o Veredito.
// A IA entra ao lado de cada Item como dica curta (classificação + evidências
// com timestamp clicável); todo o resto do que o agente produziu — critério a
// critério, justificativa, confiança, régua, log de execução — está na aba
// Dados IA. A primeira entrega tinha invertido isso: o log da IA ocupava a aba
// Checklist e o Veredito do professor não era gravado em `debriefing/scales`,
// que é de onde sai o Desempenho da estação.
interface ChecklistSkill {
  skill_id: number | string;
  name?: string;
  items?: { item_id: number | string; question?: string; scale_value?: string | null }[];
}

interface DebriefingFormProps {
  debriefingId: string | number;
  stationId: string | number;
  checklistId?: string | number;
  station?: {
    room_name?: string;
    checklist_name?: string;
    checklist_id?: string | number;
  };
  /** Ausente = trata como nao-avaliador: o default seguro e leitura. */
  isAppraiser?: boolean;
}

export default function DebriefingForm({
  debriefingId,
  stationId,
  checklistId,
  station,
  isAppraiser
}: DebriefingFormProps) {
  const navigate = useNavigate();
  const [video, setVideo] = useState("");
  const [videoId, setVideoId] = useState("");
  const [arrVideo, setArrVideo] = useState<any[]>([]);
  const [cameraMain, setCameraMain] = useState(false);
  const [cameraIndex, setCameraIndex] = useState("1");
  const [skills, setSkills] = useState<ChecklistSkill[]>([]);
  const ia = useChecklistAiResult(debriefingId);

  const loadChecklistDebriefing = async (
    checklistId: any,
    debriefingId: any
  ) => {
    await CheckListService.getChecklistDebriefing(checklistId, debriefingId)
      .then((response: any) => {
        if (response) {
          setSkills(response.data?.skills || []);
        }
      })
      .catch((e: Error) => {});
  };

  const loadDebriefingUrl = async (debriefingId: any) => {
    await DebriefingService.getDebriefingUrl(debriefingId)
      .then((response: any) => {
        if (response) {
          if (
            response.data.length > 0 &&
            response.data[0].field_name != "debriefing_video"
          ) {
            setVideo(response.data[0]?.recording_url);
            setVideoId(response.data[0]?.streaming_id || response.data[0]?.attachment_id);
            const cameraDefault = response.data[0]?.camera_main ? true : false;
            setCameraMain(cameraDefault);
            setArrVideo(response.data);
            setCameraIndex("1");
          } else {
            setVideo(response.data[0]?.static_url);
            setVideoId(response.data[0]?.attachment_id);
            setCameraMain(true);
            setCameraIndex("1");
          }
        }
      })
      .catch((e: Error) => {});
  };

  useEffect(() => {
    if (checklistId) {
      loadDebriefingUrl(debriefingId);
    }
    if (checklistId && debriefingId) {
      loadChecklistDebriefing(checklistId, debriefingId);
    }
  }, [checklistId, debriefingId]);

  // Só o avaliador da estação escreve. A release/2.0 endureceu isto no
  // formulário manual (`disabled={isAppraiser ? false : true}`, commit
  // 897742b) e a regra vale igual aqui: aluno vê em leitura, e
  // professor/coordenador/admin que NÃO é o avaliador daquela estação também
  // não dá Veredito.

  return (
    <Box>
      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        sx={{ mt: 0.5, position: "relative", px: { xs: 1, md: 3 } }}
      >
        {/* Vídeo fixo ao lado do checklist enquanto a lista rola.

            O `position: sticky` fica no GRID ITEM, não no Box interno — e isso
            é o detalhe que faz funcionar: um elemento sticky só se move dentro
            do seu bloco contêiner. No Box interno o contêiner era o próprio
            Grid item (815px de vídeo dentro de 831px de item), ou seja, zero
            folga para colar. No Grid item o contêiner passa a ser o Grid
            container, que tem a altura inteira do checklist. `alignSelf:
            flex-start` impede que o item estique e volte a ocupar tudo. */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            position: { md: "sticky" },
            top: { md: 16 },
            alignSelf: "flex-start",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "min(1240px, calc(60vh * 16 / 9))",
              mx: "auto",
              backgroundColor: palette.videoShell,
              borderRadius: `${palette.radius}px`,
              overflow: "hidden",
              boxShadow: palette.shadowSm,
            }}
          >
            <DebriefingVideo
              video={video}
              videoId={videoId}
              arrVideo={arrVideo}
              setVideo={setVideo}
              setVideoId={setVideoId}
              cameraMain={cameraMain}
              setCameraMain={setCameraMain}
              cameraIndex={cameraIndex}
              setCameraIndex={setCameraIndex}
              heightVideo="60vh"
              badge={station?.room_name}
              transcript={ia.status === "finished" ? ia.analise?.transcript : undefined}
              analysisSources={ia.status === "finished" ? ia.analise?.video_sources : undefined}
            />
          </Box>
        </Grid>

        {/* Correção oficial da estação. O `id_tab=5` é a aba Dados IA, onde
            vive o detalhe completo do agente. */}
        <Grid item xs={12} md={7} sx={{ display: "flex", flexDirection: "column" }}>
          <DebriefingChecklistView
            stationId={stationId}
            debriefingId={debriefingId}
            checklistId={checklistId}
            checklistName={station?.checklist_name}
            skills={skills}
            iaPorItem={ia.porItem}
            iaStatus={ia.status}
            iaMensagem={ia.msg}
            isAppraiser={isAppraiser}
            onVerDadosIa={isAppraiser ? () => navigate({ search: "?id_tab=5" }) : undefined}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
